/** Photographic shop: preserve the approved room, explore only within its image bounds.
 * The photograph already contains its floor and perspective. Never extend it with invented geometry.
 */
import * as THREE from "./lh-95497e0a-three.module.min.js?v=e2b5ee6bccd3";

import {expandedPanelRect, fitExpandedPanel} from "./lh-78556ebf-geometry.js?v=codex-20261027-r14";
export {expandedPanelRect, fitExpandedPanel};

export const GATE_POSES = {
  entrada: { x: 0.0, y: 1.62, z: 2.80, yaw: 0.0, pitch: 0.0, fov: 50, mode: 'room', label: 'Entrada da loja' },
  near:    { x: 0.0, y: 1.68, z: 1.30, yaw: 0.0, pitch: 0.0, fov: 50, mode: 'room', label: 'Mais perto' },
  left:    { x: -0.95, y: 1.62, z: 2.60, yaw: 0.13, pitch: 0.0, fov: 45, mode: 'room', label: 'Esquerda' },
  right:   { x: 0.95, y: 1.62, z: 2.60, yaw: -0.13, pitch: 0.0, fov: 45, mode: 'room', label: 'Direita' },
  oblique: { x: -0.82, y: 1.64, z: 2.45, yaw: -0.12, pitch: 0.005, fov: 45, mode: 'room', label: 'Vista oblíqua' },
  return:  { x: 0.0, y: 1.62, z: 2.80, yaw: 0.0, pitch: 0.0, fov: 50, mode: 'room', label: 'Regresso à entrada' },
};

export const CAMERA_POSES = GATE_POSES;

export const STATION_POSES = {
  loja:               { x: 0.0, y: 1.62, z: 2.80, yaw: 0.0, pitch: 0.0, fov: 50, mode: 'room', label: 'Vista da Loja' },
  entrada:            { x: 0.0, y: 1.62, z: 2.80, yaw: 0.0, pitch: 0.0, fov: 50, mode: 'room', label: 'Vista da Loja' },
  painel:             { x: 0.0, y: 1.68, z: 2.15, yaw: 0.0, pitch: 0.008, fov: 44, mode: 'room', label: 'Painel aproximado' },
  balcao:             { x: 0.0, y: 1.62, z: 2.15, yaw: 0.0, pitch: -0.01, fov: 45, mode: 'room', label: 'Balcão da Larrysa' },
  bolos:              { x: -1.35, y: 1.56, z: 1.85, yaw: 0.22, pitch: -0.02, fov: 44, mode: 'vitrina_bolos', label: 'Vitrina de Bolos' },
  'vitrina-bolos':    { x: -1.35, y: 1.56, z: 1.85, yaw: 0.22, pitch: -0.02, fov: 44, mode: 'vitrina_bolos', label: 'Vitrina de Bolos' },
  vitrina_bolos:      { x: -1.35, y: 1.56, z: 1.85, yaw: 0.22, pitch: -0.02, fov: 44, mode: 'vitrina_bolos', label: 'Vitrina de Bolos' },
  salgados:           { x: 1.35, y: 1.56, z: 1.85, yaw: -0.22, pitch: -0.02, fov: 44, mode: 'vitrina_salgados', label: 'Sobremesas & Salgados' },
  'vitrina-salgados': { x: 1.35, y: 1.56, z: 1.85, yaw: -0.22, pitch: -0.02, fov: 44, mode: 'vitrina_salgados', label: 'Sobremesas & Salgados' },
  vitrina_salgados:   { x: 1.35, y: 1.56, z: 1.85, yaw: -0.22, pitch: -0.02, fov: 44, mode: 'vitrina_salgados', label: 'Sobremesas & Salgados' },
};

const BOUNDS = {minX:-6.76,maxX:6.76,minZ:1.15,maxZ:3.15};
// Geometria exata do store-panorama.webp (1916 × 821) no espaço 3D:
const FRONT_W = 13.5357;
const FRONT_H = 5.80;
const FRONT_Y = 1.62;
const FRONT_Z = -3.40;

// Moldura interior do painel central em store-panorama.webp: x = 560..1364 (804px), y = 24..496 (472px)
const PANEL_U0 = 560 / 1916;
const PANEL_U1 = 1364 / 1916;
const PANEL_V0 = 24 / 821;
const PANEL_V1 = 496 / 821;

const PANEL_X0 = (PANEL_U0 - 0.5) * FRONT_W; // -2.8117 m
const PANEL_X1 = (PANEL_U1 - 0.5) * FRONT_W; // +2.8682 m
const PANEL_Y_TOP = FRONT_Y + (0.5 - PANEL_V0) * FRONT_H; // +4.3505 m
const PANEL_Y_BOT = FRONT_Y + (0.5 - PANEL_V1) * FRONT_H; // +1.0160 m
const PANEL_W_3D = PANEL_X1 - PANEL_X0; // 5.6799 m
const PANEL_H_3D = PANEL_Y_TOP - PANEL_Y_BOT; // 3.3345 m
const PANEL_CX_3D = (PANEL_X0 + PANEL_X1) / 2; // +0.02825 m
const PANEL_CY_3D = (PANEL_Y_TOP + PANEL_Y_BOT) / 2; // +2.68325 m
const PANEL_Z_3D = -3.396;

// Dimensões internas fixas do DOM #shop-panel (proporção exata 804:472)
export const PANEL_DOM_W = 884;
export const PANEL_DOM_H = 519;

/**
 * Calcula a matriz CSS matrix3d que projeta um retângulo (0..W, 0..H)
 * nos 4 cantos 2D (p0=TL, p1=TR, p2=BR, p3=BL) no ecrã.
 */
export function computeHomographyMatrix3d(W, H, p0, p1, p2, p3) {
  const x0 = p0.x, y0 = p0.y;
  const x1 = p1.x, y1 = p1.y;
  const x2 = p2.x, y2 = p2.y;
  const x3 = p3.x, y3 = p3.y;

  const dx1 = x1 - x2, dy1 = y1 - y2;
  const dx2 = x3 - x2, dy2 = y3 - y2;
  const sx = x0 - x1 + x2 - x3;
  const sy = y0 - y1 + y2 - y3;

  const det = dx1 * dy2 - dx2 * dy1;
  if (Math.abs(det) < 1e-6) return null;

  const g = (sx * dy2 - sy * dx2) / det;
  const h = (dx1 * sy - dy1 * sx) / det;
  const a = x1 - x0 + g * x1;
  const b = x3 - x0 + h * x3;
  const c = x0;
  const d = y1 - y0 + g * y1;
  const e = y3 - y0 + h * y3;
  const f = y0;

  return `matrix3d(${
    (a / W).toFixed(7)}, ${(d / W).toFixed(7)}, 0, ${(g / W).toFixed(9)}, ${
    (b / H).toFixed(7)}, ${(e / H).toFixed(7)}, 0, ${(h / H).toFixed(9)}, 0, 0, 1, 0, ${
    c.toFixed(3)}, ${f.toFixed(3)}, 0, 1)`;
}

export class BakeryScene3D {
  constructor({
    canvas,
    anchorsRoot,
    shopPanelEl,
    onSelectProduct,
    onSelectStation,
    onSelectLarrysa,
    onOpenMontra,
    onPoseChange,
  }) {
    this.canvas = canvas;
    this.anchorsRoot = anchorsRoot;
    this.shopPanelEl = shopPanelEl || document.getElementById('shop-panel');
    this.onSelectProduct = onSelectProduct;
    this.onSelectStation = onSelectStation;
    this.onSelectLarrysa = onSelectLarrysa;
    this.onOpenMontra = onOpenMontra;
    this.onPoseChange = onPoseChange;

    this.currentPose = { ...GATE_POSES.entrada };
    this.targetPose = { ...GATE_POSES.entrada };
    this.activeMode = 'room';
    this.activePoseId = 'entrada';
    this.activeKeys = new Set();
    this.interactiveMeshes = [];
    this.direction = 'cacau';
    this.panelBoardState = {
      direction: 'cacau',
      title: 'Flor Cítrica',
      subtitle: 'Bolo de laranja com uma apresentação florida',
      price: '28,00 €',
      basketCount: 0,
      basketTotal: '0,00 €',
    };
  }

  async init() {
    const renderer = new THREE.WebGLRenderer({
      canvas: this.canvas,
      antialias: true,
      powerPreference: 'high-performance',
    });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 1.5));
    renderer.setSize(window.innerWidth, window.innerHeight, false);
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.NoToneMapping;
    this.renderer = renderer;

    const scene = new THREE.Scene();
    scene.background = new THREE.Color('#3c2923');
    this.scene = scene;

    const camera = new THREE.PerspectiveCamera(
      GATE_POSES.entrada.fov,
      window.innerWidth / window.innerHeight,
      0.08,
      45
    );
    this.camera = camera;

    const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.reducedMotion = reducedMotionQuery.matches;
    reducedMotionQuery.addEventListener?.('change', (e) => {
      this.reducedMotion = e.matches;
    });

    const texLoader = new THREE.TextureLoader();
    const maxAniso = Math.min(8, renderer.capabilities.getMaxAnisotropy?.() || 4);
    const loadTex = async (url) => {
      const tex = await texLoader.loadAsync(new URL(url, import.meta.url).href);
      tex.colorSpace = THREE.SRGBColorSpace;
      tex.anisotropy = maxAniso;
      return tex;
    };

    const frontPanoramaTex = await loadTex(new URL("./lh-8b505594-store-panorama.webp?v=a9bf8d95e6dd",import.meta.url).href);
    const roomGroup = new THREE.Group();scene.add(roomGroup);this.roomGroup=roomGroup;
    this.frontMat = new THREE.MeshBasicMaterial({map:frontPanoramaTex,toneMapped:false});
    this.frontMesh = new THREE.Mesh(new THREE.PlaneGeometry(FRONT_W,FRONT_H),this.frontMat);
    this.frontMesh.position.set(0,FRONT_Y,FRONT_Z);roomGroup.add(this.frontMesh);

    // 2. ECRÃ 3D DO PAINEL CENTRAL NA PAREDE (`x: 560..1364, y: 24..496`)
    // Preenche a moldura preta no mundo WebGL 3D com a arte do Atelier de Cacau / Herbário à Mesa
    this.panelCanvas = document.createElement('canvas');
    this.panelCanvas.width = 1608;
    this.panelCanvas.height = 944;
    this.panelTexture = new THREE.CanvasTexture(this.panelCanvas);
    this.panelTexture.colorSpace = THREE.SRGBColorSpace;
    this.panelTexture.anisotropy = maxAniso;

    const panelGeo = new THREE.PlaneGeometry(PANEL_W_3D, PANEL_H_3D, 1, 1);
    const panelMat = new THREE.MeshBasicMaterial({
      map: this.panelTexture,
      side: THREE.DoubleSide,
      toneMapped: false,
    });
    this.panel3DMesh = new THREE.Mesh(panelGeo, panelMat);
    this.panel3DMesh.position.set(PANEL_CX_3D, PANEL_CY_3D, PANEL_Z_3D);
    scene.add(this.panel3DMesh);
    this._renderPanelCanvas();

    // 7. ZONAS INTERATIVAS 3D (RAYCASTING NO CENÁRIO)
    const hitMat = new THREE.MeshBasicMaterial({
      transparent: true,
      opacity: 0,
      depthWrite: false,
      side: THREE.DoubleSide,
    });
    const addHitZone = ({ w, h, x, y, z, mode, type, handle, stationId, label }) => {
      const m = new THREE.Mesh(new THREE.PlaneGeometry(w, h), hitMat);
      m.position.set(x, y, z);
      m.userData = { mode, type, handle, stationId, label };
      scene.add(m);
      this.interactiveMeshes.push(m);
    };

    addHitZone({w:PANEL_W_3D,h:PANEL_H_3D,x:PANEL_CX_3D,y:PANEL_CY_3D,z:PANEL_Z_3D+.01,mode:'room',type:'menu',label:'Ver o menu'});
    // Na loja principal: tocar na mascote Larrysa abre a conversa; tocar nas vitrinas laterais aproxima
    addHitZone({ w: 1.45, h: 1.35, x: -0.18, y: 0.22, z: -3.35, mode: 'room', type: 'larrysa', label: 'Abrir o Assistente da Bakery' });
    addHitZone({ w: 2.30, h: 1.50, x: -3.20, y: -0.15, z: -3.35, mode: 'room', type: 'station', stationId: 'bolos', label: 'Vitrina de Bolos' });
    addHitZone({ w: 2.30, h: 1.50, x: 3.30, y: -0.15, z: -3.35, mode: 'room', type: 'station', stationId: 'salgados', label: 'Sobremesas & Salgados' });

    document.body.classList.add('photographic-room');
    this._bindControls();
    this._startLoop();
  }

  setDirection(dir) {
    this.direction = dir === 'herbario' ? 'herbario' : 'cacau';
    this.panelBoardState.direction = this.direction;
    this._renderPanelCanvas();
  }

  setDisplayImage(image) {
    this.displayImage = image;
    image?.addEventListener('load', () => this._renderPanelCanvas());
    this._renderPanelCanvas();
  }

  _renderPanelCanvas() {
    if (!this.panelCanvas) return;
    const ctx = this.panelCanvas.getContext('2d');
    const W = this.panelCanvas.width, H = this.panelCanvas.height;
    ctx.fillStyle = '#30231f'; ctx.fillRect(0,0,W,H);
    const image = this.displayImage;
    if(image?.complete && image.naturalWidth) {
      const scale = Math.max(W/image.naturalWidth,H/image.naturalHeight);
      const w=image.naturalWidth*scale,h=image.naturalHeight*scale;
      ctx.drawImage(image,(W-w)/2,(H-h)/2,w,h);
    }
    ctx.fillStyle = '#30231fe8'; ctx.fillRect(0,0,W,100);
    ctx.fillStyle = '#fff1df'; ctx.font = '52px Georgia';
    ctx.fillText('Larrysa\'s Bakery',54,67);
    ctx.textAlign='right';ctx.font = '30px Arial';ctx.fillText('Ver o menu',W-54,63);ctx.textAlign='left';
    this.panelTexture.needsUpdate = true;
  }

  _syncSceneMode(mode) {
    this.activeMode = mode || 'room';
    if (this.roomGroup) this.roomGroup.visible = true;
  }

  _effectiveCameraPose(pose) {
    const stage=this.canvas?.parentElement;
    const width=stage?.clientWidth || window.innerWidth,height=stage?.clientHeight || window.innerHeight;
    const aspect=width/Math.max(1,height);
    const shortTouch=width<1100&&height<=800&&pose.mode!=='table';
    const z=Math.max(BOUNDS.minZ,Math.min(BOUNDS.maxZ,pose.z));
    const distance=z-FRONT_Z;
    const maxHalfH=Math.min(FRONT_H/2,FRONT_W/2/aspect)*.999;
    const fov=Math.min(pose.fov,shortTouch?46:pose.fov,THREE.MathUtils.radToDeg(2*Math.atan(maxHalfH/distance)));
    const halfH=Math.tan(THREE.MathUtils.degToRad(fov/2))*distance,halfW=halfH*aspect;
    return {...pose,z,fov,yaw:0,pitch:0,
      x:Math.max(-FRONT_W/2+halfW,Math.min(FRONT_W/2-halfW,pose.x)),
      y:shortTouch?FRONT_Y+FRONT_H/2-halfH:Math.max(FRONT_Y-FRONT_H/2+halfH,Math.min(FRONT_Y+FRONT_H/2-halfH,pose.y))};
  }

  setNavigationEnabled(enabled) {
    this.navigationEnabled = enabled;
    if (!enabled) Object.assign(this.targetPose,this.currentPose);
    this.activeKeys.clear();
    this.cancelDrag?.();
  }

  _canNavigate() {
    return this.navigationEnabled !== false && !this.canvas.inert;
  }

  _updateAnchors() {
    if (!this.camera) return;
    const stage = this.canvas?.parentElement || document.body;
    const w = stage.clientWidth || window.innerWidth;
    const h = stage.clientHeight || window.innerHeight;

    const panelEl = this.shopPanelEl;
    const isMobileExpanded = document.body.classList.contains('mobile-panel-expanded');
    if (panelEl) {
      const points=[[PANEL_X0,PANEL_Y_TOP],[PANEL_X1,PANEL_Y_TOP],[PANEL_X1,PANEL_Y_BOT],[PANEL_X0,PANEL_Y_BOT]]
        .map(([x,y])=>new THREE.Vector3(x,y,PANEL_Z_3D).project(this.camera))
        .map(p=>({x:(p.x*.5+.5)*w,y:(-p.y*.5+.5)*h}));
      if (isMobileExpanded) {
        const box=stage.getBoundingClientRect();
        fitExpandedPanel(panelEl,{left:box.left+Math.min(...points.map(p=>p.x)),right:box.left+Math.max(...points.map(p=>p.x)),top:box.top+Math.min(...points.map(p=>p.y)),bottom:box.top+Math.max(...points.map(p=>p.y))},{width:window.innerWidth,height:window.innerHeight,top:box.top});
      }
      const readableOnWall=w>=1100;
      panelEl.inert=false;
      panelEl.style.opacity='1';
      panelEl.style.pointerEvents=panelEl.inert?'none':'auto';
      if(isMobileExpanded||!readableOnWall){
        panelEl.style.transform='none';panelEl.style.width='';panelEl.style.height='';
      } else if(readableOnWall){
        const matrix=computeHomographyMatrix3d(PANEL_DOM_W,PANEL_DOM_H,...points);
        panelEl.style.width=`${PANEL_DOM_W}px`;panelEl.style.height=`${PANEL_DOM_H}px`;
        panelEl.style.transformOrigin='0 0';panelEl.style.transform=matrix;
        panelEl.style.setProperty('--poster-projection',matrix);
      }
    }
    // 2. Projetar hotspots diegéticos (Larrysa na frente do balcão abaixo das mãos, nunca sobre o rosto/avental)
    const anchorPositions = {
      larrysa: new THREE.Vector3(-.18,-.58,-3.35),
      cakes: new THREE.Vector3(-3.35,.05,-3.35),
      savories: new THREE.Vector3(3.45,.05,-3.35),
    };
    const map = [
      { key: 'cakes', el: document.querySelector('.hotspot-cakes') },
      { key: 'larrysa', el: document.querySelector('.hotspot-larrysa') },
      { key: 'savories', el: document.querySelector('.hotspot-savories') },
    ];
    for (const item of map) {
      if (!item.el) continue;
      if (this.activeMode !== 'room' || isMobileExpanded) {
        item.el.hidden = true;
        item.el.style.opacity = '0';
        item.el.style.pointerEvents = 'none';
        continue;
      }
      if(w<1100){
        item.el.hidden=false;item.el.style.opacity='1';item.el.style.pointerEvents='auto';
        item.el.style.left='';item.el.style.top='';continue;
      }
      const pos = anchorPositions[item.key].clone();
      pos.project(this.camera);
      if (pos.z > 1 || pos.x < -1.08 || pos.x > 1.08 || pos.y < -0.92 || pos.y > 0.85) {
        item.el.hidden = true;
        item.el.style.opacity = '0';
        item.el.style.pointerEvents = 'none';
      } else {
        const rawX = (pos.x * 0.5 + 0.5) * w;
        const sx = Math.max(96, Math.min(w - 96, rawX));
        const sy = (-pos.y * 0.5 + 0.5) * h;
        item.el.hidden = false;
        item.el.style.opacity = '1';
        item.el.style.pointerEvents = 'auto';
        item.el.style.left = `${Math.round(sx)}px`;
        item.el.style.top = `${Math.round(sy)}px`;
      }
    }
  }

  _bindControls() {
    const raycaster = new THREE.Raycaster();
    const pointer = new THREE.Vector2();
    let isDragging = false;
    let dragStart = { x: 0, y: 0, yaw: 0, pitch: 0, moved: false };

    window.addEventListener('keydown', (e) => {
      if (!this._canNavigate() || e.defaultPrevented || e.ctrlKey || e.metaKey || e.altKey) return;
      const target = e.target || document.activeElement;
      if (target?.closest?.('button,a,input,textarea,select,[contenteditable=true],dialog,[role=dialog]')) return;
      const k = e.key.toLowerCase();
      if (['w', 'a', 's', 'd', 'arrowup', 'arrowdown', 'arrowleft', 'arrowright'].includes(k)) {
        if (this.activeMode !== 'room') this._syncSceneMode('room');
        e.preventDefault();
        this.activeKeys.add(k);
      }
    });

    window.addEventListener('keyup', (e) => {
      this.activeKeys.delete(e.key.toLowerCase());
    });

    this.cancelDrag = () => { isDragging = false; };
    const release = () => { this.activeKeys.clear(); this.cancelDrag(); };
    window.addEventListener('blur', release);
    document.addEventListener('visibilitychange', () => { if (document.hidden) release(); });
    this.canvas.addEventListener('lostpointercapture', this.cancelDrag);

    this.canvas.addEventListener('pointerdown', (e) => {
      if (!this._canNavigate()) return;
      this.canvas.focus?.({preventScroll:true});
      isDragging = true;
      dragStart = {
        x: e.clientX,
        y: e.clientY,
        panX: this.targetPose.x,
        panY: this.targetPose.y,
        moved: false,
      };
      this.canvas.setPointerCapture?.(e.pointerId);
    });

    this.canvas.addEventListener('pointermove', (e) => {
      const rect = this.canvas.getBoundingClientRect();
      pointer.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      pointer.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;

      if (!this._canNavigate()) return;
      if (!isDragging) {
        raycaster.setFromCamera(pointer, this.camera);
        const hits = raycaster.intersectObjects(
          this.interactiveMeshes.filter((m) => m.userData.mode === this.activeMode),
          false
        );
        this.canvas.style.cursor = hits.length > 0 ? 'pointer' : 'grab';
        return;
      }

      const dx = e.clientX - dragStart.x;
      const dy = e.clientY - dragStart.y;
      if (Math.hypot(dx, dy) > 5) dragStart.moved = true;

      const view=this._effectiveCameraPose(this.targetPose);
      const units=2*Math.tan(THREE.MathUtils.degToRad(view.fov/2))*(view.z-FRONT_Z)/rect.height;
      const framed=this._effectiveCameraPose({...this.targetPose,x:dragStart.panX-dx*units,y:dragStart.panY+dy*units});
      Object.assign(this.targetPose,{x:framed.x,y:framed.y,yaw:0,pitch:0});
    });

    const endDrag = (e) => {
      if (!isDragging) return;
      isDragging = false;
      if (!dragStart.moved && e && this._canNavigate()) {
        const rect = this.canvas.getBoundingClientRect();
        pointer.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
        pointer.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
        raycaster.setFromCamera(pointer, this.camera);
        const hits = raycaster.intersectObjects(
          this.interactiveMeshes.filter((m) => m.userData.mode === this.activeMode),
          false
        );
        if (hits.length > 0) {
          const data = hits[0].object.userData;
          if (data.type === 'product' && data.handle) {
            this.onSelectProduct?.(data.handle);
          } else if (data.type === 'menu') {
            this.onOpenMontra?.();
          } else if (data.type === 'larrysa') {
            this.onSelectLarrysa?.();
          } else if (data.type === 'station' && data.stationId) {
            this.onSelectStation?.(data.stationId);
          }
        }
      }
    };

    this.canvas.addEventListener('pointerup', endDrag);
    this.canvas.addEventListener('pointercancel', () => {
      isDragging = false;
    });

    this.canvas.addEventListener(
      'wheel',
      (e) => {
        if (!this._canNavigate()) return;
        e.preventDefault();
        if (this.activeMode !== 'room') {
          if (e.deltaY > 25) {
            this.goToStation('loja');
          }
          return;
        }
        const step = Math.max(-.75,Math.min(.75,e.deltaY * .0022));
        this.nudgeWalk(-step);
        this.onPoseChange?.({ poseId: null, target: this.targetPose });
      },
      { passive: false }
    );

    const onResize = () => {
      const stage = this.canvas?.parentElement || document.body;
      const w = stage.clientWidth || window.innerWidth;
      const h = stage.clientHeight || window.innerHeight;
      this.camera.aspect = w / Math.max(1, h);
      const eff = this._effectiveCameraPose(this.currentPose);
      this.camera.fov = eff.fov;
      this.camera.updateProjectionMatrix();
      this.renderer.setSize(w, h, false);
      this._updateAnchors();
    };
    window.addEventListener('resize', onResize);
    onResize();
  }

  _startLoop() {
    let lastTime = performance.now();
    const animate = (now) => {
      requestAnimationFrame(animate);
      const dt = Math.min(0.05, (now - lastTime) / 1000);
      lastTime = now;

      const moveForward = this.activeKeys.has('w') || this.activeKeys.has('arrowup');
      const moveBack = this.activeKeys.has('s') || this.activeKeys.has('arrowdown');
      const moveLeft = this.activeKeys.has('a') || this.activeKeys.has('arrowleft');
      const moveRight = this.activeKeys.has('d') || this.activeKeys.has('arrowright');

      if (!this._canNavigate()) this.activeKeys.clear();
      if (this._canNavigate() && (moveForward || moveBack || moveLeft || moveRight)) {
        const speed = 2.15 * dt;
        let fwd = (moveForward ? 1 : 0) - (moveBack ? 1 : 0);
        let strafe = (moveRight ? 1 : 0) - (moveLeft ? 1 : 0);
        const len = Math.hypot(fwd, strafe) || 1;
        fwd /= len;
        strafe /= len;
        this.nudgeWalk(fwd*speed,strafe*speed);
      }

      const lerpFactor = this.reducedMotion ? 1.0 : Math.min(1, dt * 8.5);
      this.currentPose.x += (this.targetPose.x - this.currentPose.x) * lerpFactor;
      this.currentPose.y += (this.targetPose.y - this.currentPose.y) * lerpFactor;
      this.currentPose.z += (this.targetPose.z - this.currentPose.z) * lerpFactor;
      this.currentPose.yaw += (this.targetPose.yaw - this.currentPose.yaw) * lerpFactor;
      this.currentPose.pitch += (this.targetPose.pitch - this.currentPose.pitch) * lerpFactor;
      this.currentPose.fov += (this.targetPose.fov - this.currentPose.fov) * lerpFactor;

      const eff = this._effectiveCameraPose(this.currentPose);
      if (Math.abs(this.camera.fov - eff.fov) > 0.05) {
        this.camera.fov = eff.fov;
        this.camera.updateProjectionMatrix();
      }

      this.camera.position.set(eff.x, eff.y, eff.z);
      this.camera.rotation.set(eff.pitch, eff.yaw, 0, 'YXZ');

      this.renderer.render(this.scene, this.camera);
      this._updateAnchors();
    };
    requestAnimationFrame(animate);
  }

  async highlightProductOnCounter(product) {
    if (!product) return;
    this.panelBoardState.title = product.name || 'Flor Cítrica';
    this.panelBoardState.subtitle = product.shortDescription || product.categoryLabel || 'A nossa montra';
    this.panelBoardState.price = product.priceLabel || '28,00 €';
    this._renderPanelCanvas();
  }

  updateBoardStatus(status = {}) {
    if (status.selectedProduct) {
      this.panelBoardState.title = status.selectedProduct.name;
      this.panelBoardState.price = status.selectedProduct.priceLabel;
    }
    this.panelBoardState.basketCount = status.basketCount ?? 0;
    this.panelBoardState.basketTotal = status.basketTotal ?? '0,00 €';
    this._renderPanelCanvas();
  }

  goToStation(stationId) {
    const st = STATION_POSES[stationId] || STATION_POSES.loja;
    this._syncSceneMode(st.mode);
    Object.assign(this.targetPose, {
      x: st.x,
      y: st.y,
      z: st.z,
      yaw: st.yaw,
      pitch: st.pitch,
      fov: st.fov,
    });
    this.onPoseChange?.({ poseId: null, target: this.targetPose, stationId });
  }

  goToGatePose(poseId) {
    const p = GATE_POSES[poseId] || GATE_POSES.entrada;
    this.activePoseId = poseId;
    this._syncSceneMode(p.mode || 'room');
    Object.assign(this.targetPose, {
      x: p.x,
      y: p.y,
      z: p.z,
      yaw: p.yaw,
      pitch: p.pitch,
      fov: p.fov,
    });
    this.onPoseChange?.({ poseId, target: this.targetPose });
  }

  setPoseImmediate(poseObj, poseId = null) {
    const p = poseObj || GATE_POSES.entrada;
    this.activePoseId = poseId;
    this._syncSceneMode(p.mode || 'room');
    Object.assign(this.targetPose, {
      x: p.x ?? 0,
      y: p.y ?? 1.62,
      z: p.z ?? 2.80,
      yaw: p.yaw ?? 0,
      pitch: p.pitch ?? 0,
      fov: p.fov ?? 50,
    });
    Object.assign(this.currentPose, this.targetPose);
    const eff = this._effectiveCameraPose(this.currentPose);
    this.camera.position.set(eff.x, eff.y, eff.z);
    this.camera.rotation.set(eff.pitch, eff.yaw, 0, 'YXZ');
    this.camera.fov = eff.fov;
    this.camera.updateProjectionMatrix();
    this.renderer.render(this.scene, this.camera);
    this._updateAnchors();
    this.onPoseChange?.({ poseId, target: this.targetPose });
  }

  nudgeWalk(forwardDelta = 0, strafeDelta = 0) {
    if (!this._canNavigate()) return;
    if (this.activeMode !== 'room') this._syncSceneMode('room');
    const view=this._effectiveCameraPose(this.targetPose);
    const framed=this._effectiveCameraPose({...this.targetPose,x:view.x+strafeDelta,y:view.y,z:view.z-forwardDelta});
    Object.assign(this.targetPose,{x:framed.x,y:framed.y,z:framed.z,yaw:0,pitch:0});
    this.onPoseChange?.({ poseId: null, target: this.targetPose });
  }

  setLightingMood(mood = 'tarde') {
    this.lightingMood=mood;
    this.frontMat?.color.set(({manha:'#fffdfa',tarde:'#ffffff',noite:'#f5e1cc'})[mood]||'#ffffff');
  }
}
